import { Box, Paper, Tooltip, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import {
  FolderModel,
  UpdatableFolder,
} from 'entities/folder/model/folderModel';
import EditFolderModal from 'widgets/folders/editFolderModal';
import { foldersApi } from 'entities/folder/api/foldersApi';
import React, { useContext, useState } from 'react';
import { MobxContext } from 'shared/lib/contexts/mobxContext';
import { useTranslation } from 'react-i18next';
import DeleteFolderModalController, {
  DeleteFolderModalControllerActions,
} from 'widgets/folders/deleteFolderModalController';
import ErrorModal from 'shared/lib/components/modals/errorModal';
import { AxiosError } from 'axios';
import { BackErrors } from 'shared/lib/utils/globalUtils';
import { UiContext } from 'shared/lib/contexts/uiContext';
import map from 'lodash-es/map';
import { makeTransition } from 'shared/lib/utils/utils';

export type EditFolderAfterReactions = {
  afterFolderUpdate?: (updatable: UpdatableFolder) => void;
  afterFolderMerge?: (
    resultFolderModel: UpdatableFolder,
    deletedFoldersIds: string[]
  ) => void;
} & DeleteFolderModalControllerActions;

export default observer(function EditFolderModalController({
  updatable,
  afterFolderDelete,
  afterFolderUpdate,
  afterFolderMerge,
  afterFolderCreate,
  handleClose,
}: {
  updatable: UpdatableFolder;
  handleClose: () => void;
  afterFolderCreate?: () => void;
} & EditFolderAfterReactions) {
  const {
    spacings: { sp },
  } = useContext(UiContext);
  const [isDeleting, setIsDeleting] = useState(false);
  const { mobxStore } = useContext(MobxContext);
  const { t } = useTranslation();

  const [isDublicate, setIsDublicate] = useState(false);
  const [mergeError, setMergeError] = useState<{
    isError: boolean;
    namesList: string[];
  }>({
    isError: false,
    namesList: [],
  });

  return (
    <>
      <EditFolderModal
        isOpen={true}
        name={updatable.name}
        mergePlacementParams={{
          initialFolderId: updatable.parent_id,
          initialSectionId: updatable.section_id,
          filterFoldersById: [updatable.id],
        }}
        afterFolderCreate={afterFolderCreate}
        handleClose={handleClose}
        onDelete={() => setIsDeleting(true)}
        onSubmit={async (params) => {
          try {
            if (params.folderId && params.sectionId) {
              try {
                const resultFolderData = await foldersApi.merge({
                  first_folder_id: updatable.id,
                  second_folder_id: params.folderId,
                  new_folder_name: params.name,
                  section_id: params.sectionId || '',
                });

                afterFolderMerge &&
                  afterFolderMerge(resultFolderData, [
                    updatable.id,
                    params.folderId,
                  ]);
              } catch (e) {
                const err = e as AxiosError<{
                  detail?:
                    | {
                        error: BackErrors;
                        files: string[];
                        tables: string[];
                      }
                    | string;
                }>;
                console.error(e);

                const detail = err?.response?.data?.detail;

                if (
                  typeof detail === 'string' &&
                  detail === BackErrors.FolderExists
                ) {
                  setIsDublicate(true);
                  return;
                }

                if (
                  detail &&
                  typeof detail !== 'string' &&
                  detail.error === BackErrors.EntityExists
                ) {
                  setMergeError({
                    isError: true,
                    namesList: [...detail.files, ...detail.tables],
                  });
                  return;
                }

                mobxStore.notificationsStore.errorMessage = t(
                  'notifications.errors.default'
                );
              }
            } else {
              try {
                const data = await foldersApi.update(
                  {
                    name: params.name,
                    section_id: params.sectionId,
                    parent_id: params.parent_id,
                  },
                  updatable.id
                );

                afterFolderUpdate && afterFolderUpdate(data);
              } catch (e) {
                const err = e as AxiosError<{ detail?: string }>;
                console.error(e);

                if (
                  err?.response?.status === 400 &&
                  err?.response?.data?.detail === BackErrors.FolderExists
                ) {
                  setIsDublicate(true);
                  return;
                }

                mobxStore.notificationsStore.errorMessage = t(
                  'notifications.errors.default'
                );
              }
            }
          } catch (e) {
            console.error(e);
          }
          handleClose();
        }}
      ></EditFolderModal>

      {isDeleting && (
        <DeleteFolderModalController
          id={updatable.id}
          handleClose={() => {
            setIsDeleting(false);
            handleClose();
          }}
          afterFolderDelete={afterFolderDelete}
        ></DeleteFolderModalController>
      )}

      {isDublicate && (
        <ErrorModal
          mainText="Ошибка действия"
          isOpen={true}
          onClose={() => setIsDublicate(false)}
        >
          <Typography variant="f18" sx={{ fontWeight: 300 }}>
            Действие невозможно&nbsp;&mdash; объект с&nbsp;таким названием уже
            существует по&nbsp;указанному адресу
          </Typography>
        </ErrorModal>
      )}

      {mergeError.isError && (
        <ErrorModal
          mainText="Ошибка действия"
          isOpen={true}
          onClose={() => setMergeError((curr) => ({ ...curr, isError: false }))}
        >
          <Typography variant="f18" sx={{ fontWeight: 300 }}>
            Невозможно выполнить объединение. Обнаружены файлы с&nbsp;одинаковым
            наименованием. Переименуйте или удалите один из&nbsp;них
            и&nbsp;попробуйте снова
          </Typography>

          <Paper
            elevation={4}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: sp.s40,
              py: sp.s60,
              borderRadius: '16px',
            }}
          >
            <Typography
              color="custom.textInactive"
              sx={{ fontWeight: 500, px: sp.s60 }}
              variant="f18"
            >
              Выявленные файлы:
            </Typography>

            <Box
              sx={{ width: 1, height: '1px', bgcolor: 'custom.separatorLine' }}
            ></Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                px: sp.s60,
              }}
            >
              {map(mergeError.namesList, (name, i) => {
                return (
                  <Tooltip
                    placement="top-start"
                    arrow
                    slotProps={{
                      tooltip: {
                        sx: {
                          'bgcolor': 'customBackgrounds.elevation5',
                          'color': 'text.primary',
                          'p': sp.s30,
                          'borderRadius': '16px',
                          '& .MuiTooltip-arrow': {
                            color: 'customBackgrounds.elevation5',
                          },
                        },
                      },
                    }}
                    sx={{ bgcolor: 'red' }}
                    key={i}
                    title={name}
                  >
                    <Typography
                      sx={{
                        'overflow': 'hidden',
                        'whiteSpace': 'nowrap',
                        'textOverflow': 'ellipsis',
                        'py': sp.s20,
                        'cursor': 'pointer',
                        'transition': makeTransition(['color'], {
                          duration: '125ms',
                        }),
                        '&:hover': {
                          color: 'primary.main',
                        },
                      }}
                      color="text.secondary"
                    >
                      {name}
                    </Typography>
                  </Tooltip>
                );
              })}
            </Box>
          </Paper>
        </ErrorModal>
      )}
    </>
  );
});
